import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { SYS_BREAKPOINT_LG } from '@oneaudi/unified-web-common';
import type { AssetType, FeatureAppMeta } from '../../../types';
import { Image } from './Image';
import type { VideoPlayerHandle } from './Video';
import { Video } from './Video';

interface SideBySideProps {
  asset: AssetType;
  children: React.ReactNode;
  sectionIndex: number;
  meta: FeatureAppMeta;
}

const StyledSideBySideContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    flex-direction: row;
    column-gap: var(--spacing-relative-lg);
  }
`;

const TextChild = styled.div`
  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    flex: 66%;
  }
`;

const StyledMediaChild = styled.div`
  margin-top: var(--spacing-relative-lg);

  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    flex: 34%;
    margin-top: inherit;
  }
`;

export const SideBySide = forwardRef<VideoPlayerHandle, SideBySideProps>(
  ({ asset, children, sectionIndex, meta }, ref) => {
    return (
      <StyledSideBySideContainer>
        <TextChild>{children}</TextChild>
        <StyledMediaChild>
          <UeContainer propertyPath={`sections.${sectionIndex}.asset`} label="Media">
            {asset.assetType === 'image' && (
              <UeReference
                propertyPath={`sections.${sectionIndex}.asset`}
                label="Images"
                behavior="component"
              >
                <Image asset={asset} />
              </UeReference>
            )}
          </UeContainer>
          <UeContainer propertyPath={`sections.${sectionIndex}.asset`} label="Media">
            {asset.assetType === 'video' && (
              <UeReference
                propertyPath={`sections.${sectionIndex}.asset`}
                behavior="component"
                label="Videos"
              >
                <UeElement type="media" property="assetSrc" label="Video">
                  <Video ref={ref} asset={asset} sectionIndex={sectionIndex} meta={meta} />
                </UeElement>
              </UeReference>
            )}
          </UeContainer>
        </StyledMediaChild>
      </StyledSideBySideContainer>
    );
  },
);
